/**
 * BoutonCloturePeriode — bouton « Clôturer la période » partagé par les briques.
 * Désactivé avant la date de verrouillage automatique. Modale d'avertissement
 * fort avant clôture. Après clôture, affiche l'état verrouillé (cadenas).
 * Conforme mockup_cloture_cote_brique (3 états).
 */
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { Alert } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { Lock, Clock } from 'lucide-react';
import type { RhPeriodePaie } from '@roronoa-collectivites/shared-types';

const MOIS = ['', 'janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/**
 * @param service clé service de la vue paie ('service_technique', 'grc',
 *   'scolaire_cantine', 'petite_enfance', 'urbanisme', 'comptable')
 * @param annee/mois période visée (par défaut le mois courant)
 */
export function BoutonCloturePeriode({ service, annee, mois, onDone }: {
  service: string; annee?: number; mois?: number; onDone?: () => void;
}) {
  const now = new Date();
  const an = annee ?? now.getFullYear();
  const mo = mois ?? now.getMonth() + 1;
  const [periode, setPeriode] = useState<RhPeriodePaie | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function charger() {
    try { setPeriode(await api.rh.periode(an, mo)); } catch { /* silencieux : la brique reste utilisable */ }
  }
  useEffect(() => { void charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [an, mo]);

  const svcState = periode?.services.find((s) => s.service === service);
  const dejaCloture = svcState?.statut === 'cloture' || periode?.statut === 'cloture';
  const dateVerrou = periode?.dateVerrouAuto;
  const avantDate = dateVerrou ? new Date() < new Date(`${dateVerrou}T00:00:00`) : true;

  async function cloturer() {
    setBusy(true); setError(null);
    try {
      await api.rh.cloturerService(service, an, mo);
      setDone(true); setOpen(false); await charger(); onDone?.();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }

  if (dejaCloture || done) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-r-lg border border-border bg-surface/40 px-3 py-2 text-sm text-ink-subtle">
        <Lock className="h-4 w-4" /> {MOIS[mo]} clôturé
      </span>
    );
  }

  return (
    <>
      <Button variant="secondary" disabled={avantDate} onClick={() => setOpen(true)}
        title={avantDate && dateVerrou ? `Disponible le ${new Date(`${dateVerrou}T00:00:00`).toLocaleDateString('fr-FR')}` : undefined}>
        {avantDate ? <Clock className="h-4 w-4" /> : <Lock className="h-4 w-4" />} Clôturer {MOIS[mo]}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} title={`Clôturer ${MOIS[mo]} ${an}`}
        footer={<>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
          <Button variant="danger" loading={busy} onClick={() => void cloturer()}><Lock className="h-4 w-4" /> Confirmer la clôture</Button>
        </>}>
        <div className="space-y-3">
          {error && <Alert variant="error">{error}</Alert>}
          <Alert variant="warning">
            <strong>Attention :</strong> après clôture, vous ne pourrez plus modifier les plannings de {MOIS[mo]} pour votre service. Les données partent en paie. Seuls les arrêts maladie / AT resteront possibles via la RH.
          </Alert>
          <p className="text-sm text-ink-muted">Assurez-vous d'avoir bien transmis toutes vos heures à la paie avant de clôturer.</p>
        </div>
      </Dialog>
    </>
  );
}
